import { RetentionReferenceStatuses, TrackingModes } from './txTrackerConsts'

/**
 * Reads the tracking stamp ({ trackingMode, referenceId }) off a scan's raw
 * treatmentTrackerData. Never throws: an unreadable payload reads as unstamped,
 * which behaves exactly like an active-treatment scan (the pre-retention default).
 */
export const readTrackingStamp = treatmentTrackerData => {
  try {
    const { trackingMode, referenceId } = JSON.parse(treatmentTrackerData || '{}')
    return { trackingMode, referenceId }
  } catch (ex) {
    return {}
  }
}

/**
 * Parses a scan's raw treatmentTrackerData for rendering. Never throws: a malformed
 * persisted payload must degrade the view, not crash the modal that hosts it.
 */
export const parseTxTrackerData = treatmentTrackerData => {
  try {
    const parsed = JSON.parse(treatmentTrackerData || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (ex) {
    return {}
  }
}

export const isRetentionStamp = stamp => stamp?.trackingMode === TrackingModes.Retention

/**
 * Two results belong to the same history only when they were measured against the same
 * baseline: retention results share a reference set, everything else shares the active plan.
 */
export const isSameTrackingContext = (stampA, stampB) =>
  isRetentionStamp(stampA) === isRetentionStamp(stampB) &&
  (!isRetentionStamp(stampA) || stampA?.referenceId === stampB?.referenceId)

/**
 * True when the treatment's retention stack already holds a usable (pending or ready)
 * active reference. The status-change prompt exists to seed the first reference;
 * re-entering Retention reuses the stack, so an existing usable reference means the
 * prompt must not offer to mint another one. An unreadable non-empty stack also counts
 * as usable: prompting against a stack we cannot read risks stacking a duplicate
 * planned-final reference on top of a working one. A failed or empty stack re-prompts,
 * so a doctor whose earlier setup failed gets another chance.
 */
export const hasUsableRetentionReference = txTrackerRetentionStls => {
  if (!txTrackerRetentionStls) {
    return false
  }

  try {
    const parsed = JSON.parse(txTrackerRetentionStls)
    if (!parsed || typeof parsed !== 'object') {
      return false
    }

    const activeStatus = parsed.references?.[parsed.activeReferenceId]?.status
    return activeStatus === RetentionReferenceStatuses.Pending || activeStatus === RetentionReferenceStatuses.Ready
  } catch (ex) {
    return true
  }
}
